import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import clsx from "clsx";

import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
const LogoutPage = lazy(() => import("./pages/Logout/Logout"));
import ProtectedRoutes from "./components/ProtectedRoute/ProtectedRoute";
const CategoriesPage = lazy(() => import("./pages/Categories/Categories"));
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"));
const PaymentMethodsPage = lazy(
  () => import("./pages/PaymentMethods/PaymentMethods"),
);
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));
const AccountsPage = lazy(() => import("./pages/Accounts/AccountsPage"));
const VoiceAssistantsPage = lazy(
  () => import("./pages/VoiceAssistants/VoiceAssistantsPage"),
);

const Reports = lazy(() => import("./pages/Transactions/Reports"));
const PasswordRecoveryPage = lazy(
  () => import("./pages/PasswordRecovery/PasswordRecoveryPage"),
);
const ResetCachePage = lazy(
  () => import("./pages/ResetCachePage/ResetCachePage"),
);
const TransactionsColorsPage = lazy(
  () => import("./pages/Colors/TransactionsColors"),
);
const RecurringTransactionsPage = lazy(
  () => import("./pages/RecurringTransactionsPage/RecurringTransactionsPage"),
);
const SourcesPage = lazy(() => import("./pages/Sources/SourcesPage"));
const PrivacyPolicyPage = lazy(
  () => import("./pages/PrivacyPolicyPage/PrivacyPolicyPage"),
);
const TermsAndConditionsPage = lazy(
  () => import("./pages/TermsAndConditionsPage/TermsAndConditionsPage"),
);
const HelpCenterPage = lazy(
  () => import("./pages/HelpCenterPage/HelpCenterPage"),
);
const NewTransactionPage = lazy(
  () => import("./pages/NewTransaction/NewTransactionPage"),
);
const NotFound404 = lazy(() => import("./pages/NotFound404/NotFound404"));
const RegisterAccountPage = lazy(
  () => import("./pages/RegisterAccount/RegisterAccount"),
);
const LoginPage = lazy(() => import("./pages/Login/Login"));
const Transactions = lazy(() => import("./pages/Transactions/Transactions"));
const AttachmentsPage = lazy(
  () => import("./pages/AttachmentsPage/AttachmentsPage"),
);
const NotificationsPage = lazy(
  () => import("./pages/NotificationsPage/NotificationsPage"),
);
const MyNetworkPage = lazy(() => import("./pages/MyNetwork/MyNetworkPage"));
const NotesPage = lazy(() => import("./pages/Notes/NotesPage"));
const NotePage = lazy(() => import("./pages/Notes/NotePage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage/DashboardPage"));
const LoansPage = lazy(() => import("./pages/LoansPage/LoansPage"));
const NewLoanPage = lazy(() => import("./pages/LoansPage/NewLoanPage"));
const ExtensionsPage = lazy(
  () => import("./pages/ExtensionsPage/ExtensionsPage"),
);

const AutoMenuPage = lazy(
  () => import("./auto/pages/AutoMenuPage/AutoMenuPage"),
);
const AutoAccountsPage = lazy(
  () => import("./auto/pages/AutoAccountsPage/AutoAccountsPage"),
);
const AutoTransactionsPage = lazy(
  () => import("./auto/pages/AutoTransactionsPage/AutoTransactionsPage"),
);
const AutoLanguagePage = lazy(
  () => import("./auto/pages/AutoLanguagePage/AutoLanguagePage"),
);
const AutoLogoutPage = lazy(
  () => import("./auto/pages/AutoLogoutPage/AutoLogoutPage"),
);
const AutoExitPage = lazy(
  () => import("./auto/pages/AutoExitPage/AutoExitPage"),
);
const AutoTransactionPage = lazy(
  () => import("./auto/pages/AutoTransactionPage/AutoTransactionPage"),
);

import LoadingWithTextPage from "./components/LoadingWithText/LoadingWithTextPage";

export const LeRoutes: React.FC = () => {
  return (
    <>
      <ScrollToTop />

      <Suspense
        fallback={
          <div
            className={clsx(["flex", "h-full", "w-full", "fixed", "inset-0"])}
          >
            <LoadingWithTextPage />
          </div>
        }
      >
        <Routes>
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditionsPage />}
          />

          <Route element={<ProtectedRoutes />}>
            <Route path="/auto" element={<AutoMenuPage />} />
            <Route path="/auto/accounts" element={<AutoAccountsPage />} />
            <Route
              path="/auto/transactions"
              element={<AutoTransactionsPage />}
            />
            <Route
              path="/auto/transactions/:transactionName"
              element={<AutoTransactionPage />}
            />
            <Route path="/auto/language" element={<AutoLanguagePage />} />
            <Route path="/auto/logout" element={<AutoLogoutPage />} />
            <Route path="/auto/exit" element={<AutoExitPage />} />
            <Route path="/dashboard/:account?" element={<DashboardPage />} />
            <Route path="/settings/cache" element={<ResetCachePage />} />
            <Route
              path="/recurring/:account?"
              element={<RecurringTransactionsPage />}
            />
            <Route path="/loans/:account?" element={<LoansPage />} />
            <Route path="/loan/:account?/:loan?" element={<NewLoanPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route
              path="/notifications/:section/:scope"
              element={<NotificationsPage />}
            />
            <Route
              path="/settings/voice-assistants"
              element={<VoiceAssistantsPage />}
            />
            <Route path="/settings/extensions" element={<ExtensionsPage />} />
            <Route
              path="/settings/colors"
              element={<TransactionsColorsPage />}
            />
            <Route path="/settings/contacts" element={<MyNetworkPage />} />
            <Route path="/settings/help" element={<HelpCenterPage />} />
            <Route
              path="/shared-transactions"
              element={<RecurringTransactionsPage />}
            />
            {/* <Route path="/notes" element={<NotesPage />} /> */}
            {/* <Route path="/notes/:noteId" element={<NotePage />} /> */}
            <Route path="/reports/:account?" element={<Reports />} />
            <Route path="/settings/" element={<SettingsPage />} />
            <Route path="/settings/accounts" element={<AccountsPage />} />

            <Route path="/settings/categories" element={<CategoriesPage />} />
            <Route path="/settings/sources" element={<SourcesPage />} />

            <Route
              path="/settings/payment-methods"
              element={<PaymentMethodsPage />}
            />
            <Route path="/settings/profile" element={<ProfilePage />} />
            <Route path="/transactions/:account?" element={<Transactions />} />
            <Route path="/files/:account?" element={<AttachmentsPage />} />
            <Route
              path="/transaction/:account?/:transaction?"
              element={<NewTransactionPage />}
            />
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/404" element={<NotFound404 />} />
            <Route path="*" element={<NotFound404 />} />
          </Route>
          <Route path="/password-recovery" element={<PasswordRecoveryPage />} />

          <Route path="/register" element={<RegisterAccountPage />} />

          <Route path="/" element={<LoginPage />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default LeRoutes;
